import {
    HttpErrorResponse,
    HttpHandler,
    HttpInterceptor,
    HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, throwError, switchMap, from } from 'rxjs';
import { AuthService } from '../_auth/auth.service';
import { SnackbarService } from '../_services/snackbar/snackbar.service';
import { KeyService } from '../_services/_utils/key.service';
import { environment } from '../../environments/environment';
import { KeycloakService } from '@inoventoryapps/keycloak-angular';
import { SERVICE_URL } from '../_urls/service.urls';

@Injectable()
export class HttpErrorHandlerInterceptor implements HttpInterceptor {
    constructor(
        private authService: AuthService,
        private snackbarService: SnackbarService,
        private keyService: KeyService,
        private keyclockService: KeycloakService
    ) {}

    intercept(req: HttpRequest<any>, next: HttpHandler): any {
        return next.handle(req).pipe(
            catchError((error: HttpErrorResponse) => {
                // Handle encrypted errors
                if (error.headers.get('x-encrypted') && error.error?.data) {
                    return from(
                        this.keyService.getServerPublicKey(
                            `${this.getBaseUrl(req)}${SERVICE_URL.CRYPTO_JWKS}`,
                            req.url
                        )
                    ).pipe(
                        switchMap((serverPublicKey: any) =>
                            from(
                                this.keyclockService.decrypt(
                                    { publicKey: serverPublicKey },
                                    error.error['data'],
                                    error.headers.get('x-crypto-enabled')
                                )
                            ).pipe(
                                switchMap((decodedError: any) => {
                                    error = new HttpErrorResponse({
                                        error: decodedError,
                                        headers: error.headers,
                                        status: error.status,
                                        statusText: error.statusText,
                                        url: error.url || undefined,
                                    });
                                    this.handleError(error);
                                    return throwError(() => error);
                                }),
                                catchError((decryptError: any) => {
                                    console.error(
                                        'Decryption error:',
                                        decryptError
                                    );
                                    this.handleError(error);
                                    return throwError(() => error);
                                })
                            )
                        ),
                        catchError((keyError: any) => {
                            console.error('Key fetch error:', keyError);
                            this.handleError(error);
                            return throwError(() => error);
                        })
                    );
                }

                // Handle non-encrypted errors
                this.handleError(error);
                return throwError(() => error);
            })
        );
    }

    private getBaseUrl(req: HttpRequest<any>): string {
        return req.url.includes(SERVICE_URL.KEYCLOCK_SERVICE)
            ? environment.keyClockApiBaseUrl
            : environment.fdmApiBaseUrl;
    }

    private handleError(error: HttpErrorResponse): void {
        switch (error.status) {
            case 400:
                this.handleClientErrors(error);
                break;
            case 401:
                this.snackbarService.errorSnackbar('Session Expired');
                break;
            case 403:
                this.snackbarService.errorSnackbar('Access Denied');
                break;
            case 404:
                this.snackbarService.errorSnackbar('Api Not Found');
                break;
            case 500:
                this.snackbarService.errorSnackbar('Internal Server Error');
                break;
            default:
                this.snackbarService.errorSnackbar(error.message);
                break;
        }
    }
    private handleClientErrors(error: HttpErrorResponse) {
        if (error.status !== 400) return;

        const code = error?.error?.responseCode;
        const msg = error?.error?.responseMsg;

        if (code === '653' && msg) {
            this.snackbarService.errorSnackbar(msg || '');

            return;
        } else {
            this.snackbarService.errorSnackbar(
                msg || error.message || 'Bad Request'
            );
            return;
        }
    }
}
